import { useState } from "react";
import { MapPin, Send, Locate } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";

const CATS = [
  { key: "any", label: "Any / Surprise me" },
  { key: "water", label: "Water-Based" },
  { key: "milk", label: "Milk-Based" },
  { key: "creamy", label: "Premium Creamy" },
  { key: "zero_sugar", label: "Reduced Sugar" },
  { key: "innovation", label: "Innovation Series" },
];

export default function RequestStock() {
  const [done, setDone] = useState(null);
  const [locating, setLocating] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    product_category: "any", product_requested: "",
    outlet_name: "", outlet_location: "",
    latitude: null, longitude: null,
    quantity_needed: "", notes: "",
  });

  const detectLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported by this browser");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        setLocating(false);
        toast.success("Location captured");
      },
      () => { setLocating(false); toast.error("Could not get location — please type it"); },
      { timeout: 8000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/stock-requests", form);
      setDone(data);
      toast.success("Request received. We'll get Pop Panda to your area.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (done) {
    return (
      <div data-testid="stock-done" className="section-pad">
        <div className="container-pp text-center max-w-2xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Request received</div>
          <h1 className="editorial-h1 text-5xl md:text-6xl mt-3">We're on it, {form.name.split(" ")[0]}.</h1>
          <p className="mt-6 text-brand-charcoal/70">Our sales team has been notified. We track every outlet request — high-demand areas move to the top of our expansion list.</p>
          {done.same_location_count > 1 && (<p className="mt-4 text-sm text-brand-charcoal/65"><strong>{done.same_location_count}</strong> requests now from <strong>{form.outlet_location}</strong>. You're not alone.</p>)}
          <a href="/" className="btn-pill btn-dark mt-8 text-sm" data-testid="stock-home">Back to Pop Panda</a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="stock-page" className="section-pad">
      <div className="container-pp grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Request Stock</div>
          <h1 className="editorial-h1 text-5xl md:text-6xl mt-3">Can't find Pop Panda nearby?</h1>
          <p className="mt-6 text-brand-charcoal/70 text-lg leading-relaxed">Tell us where you tried — your favourite shop, supermarket, school canteen, college, event — and we'll bring Pop Panda to your area.</p>
          <div className="mt-8 card-soft p-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Why this matters</div>
            <ul className="mt-3 space-y-2 text-sm text-brand-charcoal/75">
              <li>• Every request is logged by outlet & area</li>
              <li>• 3+ requests from one area = priority delivery</li>
              <li>• High-demand areas unlock dealer & franchise opportunities</li>
              <li>• You can request a specific product or just say "any"</li>
            </ul>
          </div>
        </div>

        <form className="lg:col-span-7 card-elevated p-8 space-y-5" onSubmit={submit} data-testid="stock-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Your Name</label><input data-testid="sr-name" className="pp-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Mobile</label><input data-testid="sr-phone" className="pp-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email (optional)</label><input type="email" data-testid="sr-email" className="pp-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Product Category</label>
              <select data-testid="sr-cat" className="pp-input bg-transparent" value={form.product_category} onChange={(e) => setForm({ ...form, product_category: e.target.value })}>
                {CATS.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
              </select>
            </div>
            <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Product Requested (optional)</label><input data-testid="sr-prod" className="pp-input" placeholder="e.g. Mango pop, charcoal lemonade…" value={form.product_requested} onChange={(e) => setForm({ ...form, product_requested: e.target.value })} /></div>
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Outlet Name</label><input data-testid="sr-outlet" className="pp-input" required placeholder="Big Bazaar / Sri Krishna Sweets…" value={form.outlet_name} onChange={(e) => setForm({ ...form, outlet_name: e.target.value })} /></div>
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Outlet Location</label><input data-testid="sr-loc" className="pp-input" required placeholder="Area, City" value={form.outlet_location} onChange={(e) => setForm({ ...form, outlet_location: e.target.value })} /></div>
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Quantity Needed (optional)</label><input data-testid="sr-qty" className="pp-input" placeholder="e.g. 50 pcs / weekly stock" value={form.quantity_needed} onChange={(e) => setForm({ ...form, quantity_needed: e.target.value })} /></div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">GPS Coordinates (optional)</label>
              <button type="button" data-testid="sr-gps" onClick={detectLocation} disabled={locating} className="btn-pill btn-ghost border border-brand-line text-xs mt-1 w-full">
                <Locate size={14} /> {locating ? "Locating…" : form.latitude ? `Captured: ${form.latitude.toFixed(3)}, ${form.longitude.toFixed(3)}` : "Use my location"}
              </button>
            </div>
          </div>
          <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Additional Notes</label><textarea data-testid="sr-notes" rows="3" className="pp-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <button data-testid="sr-submit" className="btn-pill btn-dark"><Send size={14} /> Request Product</button>
        </form>
      </div>
    </div>
  );
}
